import { ref, watch, type Ref } from 'vue';
import { api } from '@/lib/http';

interface SignedUrl {
  url: string;
  expiresAt: string;
  name: string;
  mimeType: string;
}

const cache = new Map<string, SignedUrl>();

export async function getFileUrl(fileId: string): Promise<SignedUrl> {
  const hit = cache.get(fileId);
  // Reuse a signed URL while it still has a minute of life left.
  if (hit && new Date(hit.expiresAt).getTime() - Date.now() > 60_000) return hit;
  const fresh = await api.get<SignedUrl>(`/files/${fileId}/url`);
  cache.set(fileId, fresh);
  return fresh;
}

export async function openFile(fileId: string) {
  const { url } = await getFileUrl(fileId);
  window.open(url, '_blank', 'noopener');
}

/** Reactive signed URL for showing a private image (logo, avatar). */
export function useFileUrl(fileId: Ref<string | null | undefined>) {
  const url = ref<string | null>(null);
  watch(
    fileId,
    async (id) => {
      url.value = null;
      if (!id) return;
      try {
        url.value = (await getFileUrl(id)).url;
      } catch {
        url.value = null;
      }
    },
    { immediate: true },
  );
  return url;
}
